import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faInfoCircle } from "@fortawesome/sharp-regular-svg-icons"
import {
  Box,
  Button,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  styled,
  Typography,
  type SelectChangeEvent,
} from "@mui/material"
import type React from "react"
import { useMemo, useState } from "react"
import { dayjsUTC } from "../../utils/dates"
import { extraColors, FONT_FAMILY } from "../../utils/theme"
import { LightTooltip } from "../LocationDetails/LocationRisk/Shared"
import {
  PHCDateRangePicker,
  RelativeDateRangeChoiceId,
} from "../Shared/PHCDateRangePicker"
import {
  DEFAULT_BIOTHREAT_FILTERS,
  useAlertCuration,
} from "./AlertCurationContext"
import { NumericInput } from "./AlertCurationStyles"
import {
  ALL_DATA_TYPES,
  ALL_DISEASES,
  ALL_LOCATIONS,
} from "./AlertCurationUtils"

const FilterLabel = styled(Typography)(({ theme }) => ({
  ...theme.typography.caption,
  color: extraColors.hint,
  marginBottom: "8px",
  textTransform: "uppercase",
  fontSize: "0.65rem",
  fontWeight: 700,
  letterSpacing: "0.05em",
}))

const FilterLabelWithTooltip: React.FC<{
  label: string
  tooltipText: string
}> = ({ label, tooltipText }) => {
  return (
    <FilterLabel>
      <LightTooltip arrow placement="top-end" title={tooltipText}>
        <FontAwesomeIcon
          icon={faInfoCircle}
          style={{
            fontSize: 10,
            color: extraColors.status.navyLight,
            paddingRight: 2,
            cursor: "help",
          }}
        />
      </LightTooltip>
      {label}
    </FilterLabel>
  )
}

const DateRangeContainer = styled(Box)(({ theme }) => ({
  ...theme.typography.body2,
  fontFamily: FONT_FAMILY,
  "& *": {
    color: extraColors.white,
    ...theme.typography.body2,
    fontFamily: FONT_FAMILY,
    fontSize: "0.7rem",
    fontWeight: 800,
  },
  "& .MuiInputBase-root": {
    height: 32,
    fontSize: "0.7rem",
    fontWeight: 800,
    backgroundColor: extraColors.translucent8,
    borderRadius: "8px",
    border: `1px solid ${extraColors.translucentWhite}`,
    "&:hover": {
      backgroundColor: extraColors.translucentWhite,
      borderColor: extraColors.translucentWhite,
    },
    "&.Mui-focused": {
      borderColor: extraColors.status.navyLight,
      backgroundColor: extraColors.translucentWhite,
    },
  },
  "& .MuiInputLabel-root": {
    color: extraColors.white,
    fontSize: "0.7rem",
    fontFamily: FONT_FAMILY,
    fontWeight: 800,
    opacity: 1,
    "&.Mui-focused": {
      color: extraColors.status.navyLight,
    },
  },
  "& .MuiTypography-root": {
    color: extraColors.white,
    opacity: 0.6,
    fontSize: "0.75rem",
    margin: "0 6px",
  },
}))

const FiltersContainer = styled(Box)(() => ({
  mb: 2,
  padding: 12,
  bgcolor: extraColors.translucent5,
  borderRadius: "12px",
  border: `1px solid ${extraColors.translucent8}`,
  backdropFilter: "blur(20px)",
}))

export const AlertCurationFilters: React.FC = () => {
  return (
    <FiltersContainer>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "flex-end",
          flexWrap: "wrap",
          rowGap: 2,
        }}
      >
        <DateRangeFilter />

        <AiScoreFilter />

        <TypeFilter />

        <DiseaseFilter />

        <LocationFilter />

        <SensitivityFilter />

        <ResetFiltersButton />
      </Stack>
    </FiltersContainer>
  )
}

const ResetFiltersButton: React.FC = () => {
  const { setAlertCurationFilters } = useAlertCuration()
  return (
    <Button
      sx={{
        fontWeight: 800,
        fontSize: "0.7rem",
      }}
      variant="contained"
      color="primary"
      onClick={() => setAlertCurationFilters(DEFAULT_BIOTHREAT_FILTERS)}
    >
      Reset Filters
    </Button>
  )
}

const DateRangeFilter: React.FC = () => {
  const { alertCurationFilters, setAlertCurationFilters } = useAlertCuration()
  const { dateRange } = alertCurationFilters
  const [initialNow] = useState(() => dayjsUTC().toISOString())

  return (
    <Box sx={{ width: 460 }}>
      <FilterLabel>Date & Time Range</FilterLabel>
      <DateRangeContainer>
        <PHCDateRangePicker
          initialValue={dateRange}
          maxDate={dayjsUTC(initialNow)}
          onChange={newDateRange =>
            setAlertCurationFilters({
              ...alertCurationFilters,
              dateRange: newDateRange,
            })
          }
          showRelativeDateRanges={true}
          visibleRelativeDateRangeChoices={[
            RelativeDateRangeChoiceId.TwentyFourHours,
            RelativeDateRangeChoiceId.ThreeDays,
            RelativeDateRangeChoiceId.SevenDays,
          ]}
        />
      </DateRangeContainer>
    </Box>
  )
}

/**
 * Converts percentage value (0-100) to decimal (0.0-1.0) for internal storage.
 * Returns null if the input value is not a valid number.
 */
const convertPercentageToDecimal = (value: string): number | null => {
  const val = parseFloat(value)
  if (!isNaN(val)) {
    return val / 100
  }
  return null
}

const AiScoreFilter: React.FC = () => {
  const { alertCurationFilters, setAlertCurationFilters } = useAlertCuration()
  const { aiScoreRange } = alertCurationFilters
  const tooltipText =
    "AI Scores greater than or equal to this number will be reflected in the table below."
  return (
    <Box sx={{ width: 110 }}>
      <FilterLabelWithTooltip label="AI Score" tooltipText={tooltipText} />
      <PercentageInput
        value={Math.round(aiScoreRange[0] * 100)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setAlertCurationFilters({
            ...alertCurationFilters,
            aiScoreRange: [
              convertPercentageToDecimal(e.target.value) ?? 0.3,
              1.0,
            ],
          })
        }
      />
    </Box>
  )
}

const LocationFilter: React.FC = () => {
  const { alertCurationFilters, setAlertCurationFilters } = useAlertCuration()
  const { locationFilter } = alertCurationFilters
  const { signals } = useAlertCuration()

  return (
    <Box sx={{ width: 140 }}>
      <FilterLabel>Location</FilterLabel>
      <Select
        size="small"
        fullWidth
        value={locationFilter}
        onChange={(e: SelectChangeEvent) =>
          setAlertCurationFilters({
            ...alertCurationFilters,
            locationFilter: e.target.value,
          })
        }
        sx={{
          height: 32,
          bgcolor: extraColors.translucent5,
          borderRadius: "8px",
          fontWeight: 800,
          fontSize: "0.7rem",
        }}
      >
        <MenuItem value={ALL_LOCATIONS}>{ALL_LOCATIONS}</MenuItem>
        {Array.from(
          new Set(
            signals
              .map(s => s.resolved_location)
              .filter((loc): loc is string => Boolean(loc))
          )
        ).map(loc => (
          <MenuItem key={loc} value={loc}>
            {loc}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

const DiseaseFilter: React.FC = () => {
  const { alertCurationFilters, setAlertCurationFilters } = useAlertCuration()
  const { disease } = alertCurationFilters
  const { signals } = useAlertCuration()

  const uniqueDiseases = useMemo(
    () =>
      Array.from(new Set(signals.flatMap(s => s.clean_disease_codes))).sort(),
    [signals]
  )

  return (
    <Box sx={{ width: 140 }}>
      <FilterLabel>Disease</FilterLabel>
      <Select
        size="small"
        fullWidth
        value={disease}
        onChange={(e: SelectChangeEvent) =>
          setAlertCurationFilters({
            ...alertCurationFilters,
            disease: e.target.value,
          })
        }
        sx={{
          height: 32,
          bgcolor: extraColors.translucent5,
          borderRadius: "8px",
          fontWeight: 800,
          fontSize: "0.7rem",
        }}
      >
        <MenuItem value={ALL_DISEASES}>{ALL_DISEASES}</MenuItem>
        {uniqueDiseases.map(name => (
          <MenuItem key={name} value={name}>
            {name}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

const TypeFilter: React.FC = () => {
  const { alertCurationFilters, setAlertCurationFilters } = useAlertCuration()
  const { type } = alertCurationFilters

  return (
    <Box sx={{ width: 180 }}>
      <FilterLabel>Type</FilterLabel>
      <Select
        size="small"
        fullWidth
        value={type}
        onChange={(e: SelectChangeEvent) =>
          setAlertCurationFilters({
            ...alertCurationFilters,
            type: e.target.value,
          })
        }
        sx={{
          height: 32,
          bgcolor: extraColors.translucent5,
          borderRadius: "8px",
          fontWeight: 800,
          fontSize: "0.7rem",
        }}
      >
        {ALL_DATA_TYPES.map(selectableType => (
          <MenuItem key={selectableType} value={selectableType}>
            {selectableType}
          </MenuItem>
        ))}
      </Select>
    </Box>
  )
}

const SensitivityFilter: React.FC = () => {
  const { alertCurationFilters, setAlertCurationFilters } = useAlertCuration()
  const { sensitivity } = alertCurationFilters
  const tooltipText =
    "This number impacts the cluster sensitivity, or the likelihood that articles will merge together to form a cluster. A higher value will increase the likelihood that articles will merge, whereas a lower value will reduce the likelihood that articles will merge."

  return (
    <Box sx={{ width: 130 }}>
      <FilterLabelWithTooltip label="Sensitivity" tooltipText={tooltipText} />
      <PercentageInput
        value={Math.round(sensitivity * 100)}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setAlertCurationFilters({
            ...alertCurationFilters,
            sensitivity: Number(e.target.value) / 100,
          })
        }
      />
    </Box>
  )
}

const PercentageInput: React.FC<{
  value: number
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}> = ({ value, onChange }) => {
  return (
    <NumericInput
      type="number"
      value={value}
      onChange={onChange}
      slotProps={{
        htmlInput: { step: 1, min: 0, max: 100 },
        input: {
          endAdornment: (
            <InputAdornment
              position="end"
              sx={{ marginRight: 0, padding: 0, marginLeft: 0.25 }}
            >
              <Typography
                variant="small1Bold"
                sx={{ fontWeight: 700, padding: 0, margin: 0 }}
              >
                %
              </Typography>
            </InputAdornment>
          ),
        },
      }}
    />
  )
}
